import React, { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  getAvailableRealEstateTypes,
  getAvailableTransactions,
  getBusinessChildren,
  segmentationTypeArray,
} from './segmentation-model'
import { resolveEndpoint } from './resolveEndpoint'
import { RealEstateCategory, SegmentationType, TransactionType } from '../../../@types/types'
import { trackSegmentationEvent, trackSegmentationPageView } from './segmentationTracking'
import { ButtonRounded, Chip, Notification, PageHead, PopoverBeta, PopoverContainer, PopoverTarget } from 'is24-corecss'
import { IconS24ArrowRight16, IconS24CrossCircle24Filled, IconS24InfoCircle24 } from '@is24/cosma-ui-icons'
import { useFormState } from '../insertionPage/wizard/useFormState'
import { useAppDispatch } from '../../../app/hooks'
import type { IdentityChip, IntentChip } from '../../../redux/reducers/segmentation/segmentationFeature'
import {
  deselectIdentityChip,
  deselectIntentChip,
  deselectPropertyTypeChip,
  deselectShortTermAccommodation,
  hideBusinessCategories,
  selectIdentityChip,
  selectIntentChip,
  selectPropertyTypeChip,
  selectShortTermAccommodation,
  setValidationErrors,
  showBusinessCategories,
} from '../../../redux/reducers/segmentation/segmentationFeature'
import { useSegmentationState } from './useSegmentationState'
import { validateSegmentationForm } from './segmentationValidation'
import { SegmentationBoxInfo } from './SegmentationBoxInfo'
import { RoleTooltipContent } from './RoleTooltipContent'
import { TransactionTooltipContent } from './TransactionTooltipContent'
import { FooterNotes } from './FooterNotes'
import styles from './SegmentationPage.module.css'
import camelCase from 'camelcase'

const SegmentationPage: React.FC = () => {
  const { t } = useTranslation()
  const { increaseSubmitCount, formState } = useFormState()
  const dispatch = useAppDispatch()

  // Get all segmentation state from custom hook
  const {
    selectedSegmentation,
    isShortTermAccommodation,
    showBusinessCategories: showBusinessCategoriesState,
    selectedPropertyTypes,
    validationErrors,
    selectedTransaction,
    selectedRealEstate,
    selectedBusinessType,
  } = useSegmentationState()

  useEffect(() => {
    trackSegmentationPageView()
  }, [])

  const handleBackButtonClick = () => {
    trackSegmentationEvent({
      eventName: 'back_button_click',
    })
    window.history.back()
  }

  const handleSegmentationClick = (segmentation: SegmentationType) => {
    // Allow deselection: if clicking the same option, clear the selection
    if (segmentation === selectedSegmentation) {
      dispatch(deselectIdentityChip())
    } else {
      dispatch(selectIdentityChip({ identity: segmentation as IdentityChip }))

      // Clear dependent selections only when changing to a new selection
      dispatch(deselectIntentChip())
      if (isShortTermAccommodation) {
        dispatch(deselectShortTermAccommodation())
      }
      if (showBusinessCategoriesState) {
        dispatch(hideBusinessCategories())
      }
      // Clear property type selections
      if (selectedPropertyTypes.length > 0) {
        selectedPropertyTypes.forEach((pt) => dispatch(deselectPropertyTypeChip({ propertyType: pt })))
      }
    }
  }

  const handleTransactionClick = (transaction: TransactionType | 'SHORT_TERM_ACCOMMODATION') => {
    // Allow deselection: if clicking the same option, clear the selection
    if (transaction === 'SHORT_TERM_ACCOMMODATION') {
      if (selectedTransaction === 'SHORT_TERM_ACCOMMODATION') {
        // Deselect SHORT_TERM_ACCOMMODATION
        dispatch(deselectShortTermAccommodation())
      } else {
        // Select SHORT_TERM_ACCOMMODATION
        dispatch(selectShortTermAccommodation())

        // Clear property type selections only when changing to a new selection
        if (selectedPropertyTypes.length > 0) {
          selectedPropertyTypes.forEach((pt) => dispatch(deselectPropertyTypeChip({ propertyType: pt })))
        }
        if (showBusinessCategoriesState) {
          dispatch(hideBusinessCategories())
        }
      }
    } else {
      // Regular transaction type
      if (transaction === selectedTransaction) {
        dispatch(deselectIntentChip())
      } else {
        dispatch(selectIntentChip({ intent: transaction as IntentChip }))

        // Clear property type selections only when changing to a new selection
        if (selectedPropertyTypes.length > 0) {
          selectedPropertyTypes.forEach((pt) => dispatch(deselectPropertyTypeChip({ propertyType: pt })))
        }
        if (showBusinessCategoriesState) {
          dispatch(hideBusinessCategories())
        }
      }
    }
  }

  const handleNewRealEstateClick = (
    realEstateCategory: 'BUSINESS' | RealEstateCategory,
    isBusinessCategory = false
  ) => {
    if (isBusinessCategory) {
      // Clicking on business subcategory - allow deselection if clicking the same option
      if (realEstateCategory === selectedBusinessType) {
        dispatch(deselectPropertyTypeChip({ propertyType: realEstateCategory as RealEstateCategory }))
      } else {
        dispatch(selectPropertyTypeChip({ propertyType: realEstateCategory as RealEstateCategory }))
      }
    } else if (realEstateCategory === 'BUSINESS') {
      // Clicking on BUSINESS category - show business categories
      if (showBusinessCategoriesState) {
        dispatch(hideBusinessCategories())
      } else {
        dispatch(showBusinessCategories())
      }
    } else {
      // Clicking on regular category - allow deselection if clicking the same option
      if (realEstateCategory === selectedRealEstate) {
        dispatch(deselectPropertyTypeChip({ propertyType: realEstateCategory as RealEstateCategory }))
      } else {
        dispatch(selectPropertyTypeChip({ propertyType: realEstateCategory as RealEstateCategory }))
      }
    }
  }

  const handleStartInsertionFLow = () => {
    increaseSubmitCount()

    // Validate form using extracted validation function
    const errors = validateSegmentationForm({
      selectedSegmentation,
      selectedTransaction,
      selectedPropertyTypes,
      showBusinessCategories: showBusinessCategoriesState,
      availableRealEstateTypes,
    })

    if (Object.keys(errors).length > 0) {
      dispatch(setValidationErrors(errors))

      // Track unsuccessful continue click with error reason
      const errorReason = Object.keys(errors)
        .map((key) => key)
        .join(', ')

      trackSegmentationEvent({
        eventName: 'unsuccessful_continue_click',
        selectedSegmentation,
        transactionType: selectedTransaction,
        realEstateCategory: selectedPropertyTypes[0] || null,
        errorReason,
      })
      return
    }

    // Handle SHORT_TERM_ACCOMMODATION special case
    if (selectedTransaction === 'SHORT_TERM_ACCOMMODATION') {
      trackSegmentationEvent({
        eventName: 'continue_button_click',
        selectedSegmentation,
        transactionType: 'SHORT_TERM_ACCOMMODATION',
        realEstateCategory: RealEstateCategory.SHORT_TERM_ACCOMMODATION,
      })

      window.location.href = resolveEndpoint(
        selectedSegmentation!,
        TransactionType.RENT,
        RealEstateCategory.SHORT_TERM_ACCOMMODATION
      )
      return
    }

    // Use the selected property type from Redux
    const finalRealEstateType = selectedPropertyTypes[0]

    trackSegmentationEvent({
      eventName: 'continue_button_click',
      selectedSegmentation,
      transactionType: selectedTransaction,
      realEstateCategory: finalRealEstateType,
    })

    window.location.href = resolveEndpoint(selectedSegmentation!, selectedTransaction!, finalRealEstateType)
  }

  const chipsGroupLabel = (translationKey: string, altMessage: string, tooltipContent?: React.ReactNode) => {
    const labelElement = (
      <span className='font-body-medium-bold'>
        <Trans i18nKey={translationKey}>{t(translationKey, altMessage)}</Trans>
      </span>
    )

    if (!tooltipContent) {
      return labelElement
    }

    return (
      <span className={'grid grid-flex grid-align-center'}>
        {labelElement}
        <PopoverContainer className='margin-left-s'>
          <PopoverTarget className='font-body-medium-regular align-left'>
            <IconS24InfoCircle24 style={{ color: '#757575' }} width={16} height={16} />
          </PopoverTarget>
          <PopoverBeta bodyPosition={'right'} arrowPosition={'center'} style={{ width: '200px' }}>
            {tooltipContent}
          </PopoverBeta>
        </PopoverContainer>
      </span>
    )
  }

  // Keep using segmentation-model helpers for UI options (they handle BUSINESS grouping correctly)
  const availableTransactions = selectedSegmentation
    ? getAvailableTransactions(selectedSegmentation)
    : [TransactionType.RENT, TransactionType.SELL, 'SHORT_TERM_ACCOMMODATION' as const]

  const availableRealEstateTypes =
    selectedSegmentation && selectedTransaction
      ? getAvailableRealEstateTypes(selectedSegmentation, selectedTransaction)
      : [RealEstateCategory.APARTMENT, RealEstateCategory.HOUSE, RealEstateCategory.GARAGE, 'BUSINESS' as const]

  return (
    <div className={`${styles.segmentationHeaderContainer} margin-top-xxl`}>
      <div id='header' className={`margin-bottom-xxl ${styles.segmentationHeader}`}>
        <PageHead
          title={t('segmentation.header')}
          subtitle={t('segmentation.subtitle')}
          buttonText={t('backButton')}
          onButtonClick={handleBackButtonClick}
        />
      </div>
      <div id='content-wrapper' className={styles.contentWrapper}>
        <div className={`grid margin-bottom-xxl ${styles.segmentationContainer}`}>
          <div className={`grid-item palm-one-whole desk-one-half gutter-l ${styles.segmentationChips}`}>
            <div className={`grid-item ${styles.segmentationChip}`}>
              <p className='margin-bottom-xl'>
                {selectedSegmentation === SegmentationType.AGENT
                  ? t('segmentation.sublineProfessional')
                  : t('segmentation.sublinePrivate')}
              </p>
              {chipsGroupLabel('segmentation.roleMessage', 'I am', <RoleTooltipContent />)}
              <div className='margin-top-xs'>
                <div className='grid-flex gutter-s' role='radiogroup'>
                  {segmentationTypeArray.map((value) => (
                    <div className='grid-item' key={value}>
                      <Chip
                        data-testid={`segmentation-type-${value.toLowerCase().replace(/_/g, '-')}`}
                        label={t(`segmentation.segmentationType.${camelCase(value)}`)}
                        active={selectedSegmentation === value}
                        onChange={() => handleSegmentationClick(value)}
                        size='small'
                        iconset='checkbox'
                      />
                    </div>
                  ))}
                </div>
                {validationErrors.identity && validationErrors.identity.length > 0 && formState.submitCount > 0 && (
                  <div role='alert' className='input-label-helper-text font-error margin-top-xs'>
                    <div className='caption caption--error' style={{ fontWeight: 'bold' }}>
                      <IconS24CrossCircle24Filled className={'caption-icon'} />
                      {t(`validation.${validationErrors.identity[0]}`)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={`grid-item ${styles.segmentationChip}`}>
              {chipsGroupLabel('segmentation.transaction', 'Ich möchte', <TransactionTooltipContent />)}
              <div className='margin-top-xs'>
                <div className='grid-flex gutter-s' role='radiogroup'>
                  {availableTransactions.map((value) => (
                    <div className='grid-item' key={value}>
                      <Chip
                        data-testid={`transaction-type-${value.toLowerCase().replace(/_/g, '-')}`}
                        label={t(`segmentation.transactionType.${camelCase(value)}`)}
                        active={selectedTransaction === value}
                        onChange={() => handleTransactionClick(value)}
                        size='small'
                        iconset='checkbox'
                      />
                    </div>
                  ))}
                </div>
                {validationErrors.intent && validationErrors.intent.length > 0 && formState.submitCount > 0 && (
                  <div role='alert' className='input-label-helper-text font-error margin-top-xs'>
                    <div className='caption caption--error' style={{ fontWeight: 'bold' }}>
                      <IconS24CrossCircle24Filled className={'caption-icon'} />
                      {t(`validation.${validationErrors.intent[0]}`)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {selectedTransaction !== 'SHORT_TERM_ACCOMMODATION' && (
              <div className={`grid-item ${styles.segmentationChip}`}>
                {availableRealEstateTypes.length > 0 && chipsGroupLabel('segmentation.realEstateType', 'Immobilientyp')}
                {availableRealEstateTypes.length > 0 && (
                  <div className='margin-top-xs'>
                    <div className='grid-flex gutter-s' role='radiogroup'>
                      {availableRealEstateTypes.map((value) => (
                        <div className='grid-item' key={value}>
                          <Chip
                            data-testid={`real-estate-type-${value.toLowerCase().replace(/_/g, '-')}`}
                            label={t(`segmentation.category.${camelCase(value)}`)}
                            active={selectedRealEstate === value}
                            onChange={() => handleNewRealEstateClick(value as 'BUSINESS' | RealEstateCategory)}
                            size='small'
                            iconset='checkbox'
                          />
                        </div>
                      ))}
                    </div>
                    {!showBusinessCategoriesState &&
                      validationErrors.propertyTypes &&
                      validationErrors.propertyTypes.length > 0 &&
                      formState.submitCount > 0 && (
                        <div role='alert' className='input-label-helper-text font-error margin-top-xs'>
                          <div className='caption caption--error' style={{ fontWeight: 'bold' }}>
                            <IconS24CrossCircle24Filled className={'caption-icon'} />
                            {t(`validation.${validationErrors.propertyTypes[0]}`)}
                          </div>
                        </div>
                      )}
                  </div>
                )}
              </div>
            )}

            {selectedRealEstate === 'BUSINESS' && (
              <div className={`grid-item ${styles.segmentationChip}`}>
                {chipsGroupLabel('segmentation.businessType', 'Gewerbetyp')}
                <div className='margin-top-xs'>
                  <div className='grid-flex gutter-s' role='radiogroup'>
                    {getBusinessChildren(selectedSegmentation ?? undefined, selectedTransaction).map((value) => (
                      <div className='grid-item' key={value}>
                        <Chip
                          data-testid={`business-type-${value.toLowerCase().replace(/_/g, '-')}`}
                          label={t(`segmentation.businessCategory.${camelCase(value)}`)}
                          active={selectedBusinessType === value}
                          onChange={() => handleNewRealEstateClick(value as RealEstateCategory, true)}
                          size='small'
                          iconset='checkbox'
                        />
                      </div>
                    ))}
                  </div>
                  {validationErrors.propertyTypes &&
                    validationErrors.propertyTypes.length > 0 &&
                    formState.submitCount > 0 && (
                      <div role='alert' className='input-label-helper-text font-error margin-top-xs'>
                        <div className='caption caption--error' style={{ fontWeight: 'bold' }}>
                          <IconS24CrossCircle24Filled className={'caption-icon'} />
                          {t(`validation.${validationErrors.propertyTypes[0]}`)}
                        </div>
                      </div>
                    )}
                </div>
              </div>
            )}

            {selectedSegmentation && selectedTransaction === 'SHORT_TERM_ACCOMMODATION' && (
              <div className={`grid-item ${styles.segmentationChip}`}>
                <Notification
                  title={''}
                  message={t('segmentation.hintShortTermAccommodation')}
                  type={'info'}
                  variant={'flat'}
                />
              </div>
            )}

            <div className={`grid-item one-whole ${styles.segmentationChip}`}>
              <ButtonRounded
                id='start-listing-flow'
                variant='filledDefault'
                size='large'
                className='palm-one-whole'
                label={t('segmentation.proceed')}
                rightIcon={<IconS24ArrowRight16 className='left-icon margin-right-s' />}
                onClick={handleStartInsertionFLow}
              />
            </div>
          </div>
          <div className={`grid-item palm-one-whole desk-one-half ${styles.segmentationCard}`}>
            <SegmentationBoxInfo type={selectedSegmentation} transaction={selectedTransaction} />
          </div>
        </div>
        <div className={`${styles.segmentationFooterNotesContainer}`}>
          <div className={`desk-one-half palm-one-whole ${styles.segmentationFooterNotes}`}>
            <FooterNotes
              selectedSegmentation={selectedSegmentation}
              selectedTransaction={selectedTransaction}
              selectedRealEstate={selectedRealEstate}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default SegmentationPage
